"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GlInterleaved2dPointBinder = void 0;
const tslib_1 = require("tslib");
const rc_js_util_1 = require("rc-js-util");
const i_gl_indexed_point2d_binder_1 = require("../i-gl-indexed-point2d-binder");
const core_1 = require("@visualization-tools/core");
/**
 * @public
 * Provides bindings for an interleaved buffer that described points in 2d.
 *
 * @remarks
 * These may optionally have per point color and size. Where either size or color is not described by the buffer, local config
 * will be supplied in instead. The type of buffer may not be changed after creation (e.g. adding size).
 **/
class GlInterleaved2dPointBinder extends core_1.AGlInstancedBinder {
    constructor(bindingDescriptor, binderConfig = { pointsToBind: 1, bindsOutput: false }) {
        super();
        this.bindingDescriptor = bindingDescriptor;
        this.binderConfig = binderConfig;
        this.binderClassificationId = i_gl_indexed_point2d_binder_1.IndexedPoint2dIdentifier;
        this.linkId = linkId;
        this.offsets = rc_js_util_1.Vec4.i32.factory.createOneEmpty();
        this.uniformsToBind = GlInterleaved2dPointBinder.getUniformsToBind(bindingDescriptor.offsets);
        this.bindings = this.getBindings(bindingDescriptor, binderConfig);
        this.specification = GlInterleaved2dPointBinder.generateProgram(bindingDescriptor, binderConfig);
        this.pointsBound = GlInterleaved2dPointBinder.isTransformConfig(binderConfig) ? 1 : binderConfig.pointsToBind;
    }
    getBinderId() {
        const preprocessorStatements = [
            "interleavedPoint2d",
            this.pointsBound,
            this.bindingDescriptor.offsets.color == null ? "noColor" : "color",
            this.bindingDescriptor.offsets.size == null ? "size" : "noSize",
        ];
        return preprocessorStatements.join("_");
    }
    getTransformBinder() {
        return new GlInterleaved2dPointTransformBinder(this.bindingDescriptor, Object.assign(Object.assign({}, this.binderConfig), { bindsOutput: true }));
    }
    link(binders) {
        const attributeState = this.bindings.pointAttributes[0].getSharableState();
        for (let i = 0, iEnd = binders.length; i < iEnd; ++i) {
            const pointAttributes = binders[i].bindings.pointAttributes;
            for (let j = 0, jEnd = pointAttributes.length; j < jEnd; ++j) {
                pointAttributes[j].link(attributeState);
            }
        }
    }
    setBufferLayout(bufferLayout) {
        const attribute = this.bindings.pointAttributes[0];
        const theirBuffer = bufferLayout.swapBuffer(attribute.getBuffer(), 0);
        attribute.setBuffer(theirBuffer);
    }
    getBufferLayout() {
        return new core_1.BufferLayout([new core_1.GlBuffer(null, Float32Array)]);
    }
    areAttributesDirty(entity) {
        const changeId = this.getChangeId(entity);
        return this.bindings.pointAttributes[0].getBuffer().changeId !== changeId;
    }
    initialize(componentRenderer) {
        const pointAttributes = this.bindings.pointAttributes;
        for (let i = 0, iEnd = pointAttributes.length; i < iEnd; ++i) {
            pointAttributes[i].initialize(componentRenderer);
        }
        if (this.uniformsToBind & EUniformBinding.BindDisplaySettings) {
            this.bindings.displayConfigUniform.initialize(componentRenderer);
        }
        if (this.uniformsToBind & EUniformBinding.BindSizeTransform) {
            this.bindings.sizeTransformUniform.initialize(componentRenderer);
        }
        this.bindings.offsetUniform.initialize(componentRenderer);
    }
    updateData(entity) {
        const data = entity.data;
        const changeId = this.getChangeId(entity);
        this.bindings.pointAttributes[0].setData(data.getInterleavedBuffer(), changeId);
        if (this.uniformsToBind & EUniformBinding.BindDisplaySettings) {
            this.bindings.displayConfigUniform.setData(entity.graphicsSettings.pointDisplay, changeId);
        }
        if (this.uniformsToBind & EUniformBinding.BindSizeTransform) {
            const transform = entity.graphicsSettings.pointSizeNormalizer.getSizeToPixelTransform();
            this.bindings.sizeTransformUniform.setData(transform, changeId);
        }
        this.offsets[0] = data.offsets.x;
        this.offsets[1] = data.offsets.y;
        if (data.offsets.size != null) {
            this.offsets[2] = data.offsets.size;
        }
        if (data.offsets.color != null) {
            this.offsets[3] = data.offsets.color;
        }
        this.bindings.offsetUniform.setData(this.offsets, entity.changeId);
    }
    overrideColors(componentRenderer, entity, changeId) {
        const highlightedSegments = entity.graphicsSettings.pointDisplay.highlightedSegments;
        const colorOffset = entity.data.offsets.color;
        if (highlightedSegments == null || colorOffset == null) {
            return;
        }
        const pointAttribute = this.bindings.pointAttributes[0];
        const blockByteSize = entity.data.getBlockByteSize();
        const byteOffset = colorOffset * Float32Array.BYTES_PER_ELEMENT;
        const overrides = GlInterleaved2dPointBinder.generateOverrides(entity, highlightedSegments);
        for (let i = 0, iEnd = overrides.length; i < iEnd; ++i) {
            const override = overrides[i];
            pointAttribute.setSubBufferData(componentRenderer, override[0] * blockByteSize + byteOffset, override[1], changeId, i);
        }
    }
    updatePointers(entity) {
        this.setPointers(entity.data.getStart(), entity.data.getBlockByteSize());
    }
    setPointers(startIndex, blockByteSize) {
        const pointAttributes = this.bindings.pointAttributes;
        const byteOffsetToStart = startIndex * blockByteSize;
        const byteStride = this.pointsBound * blockByteSize;
        for (let i = 0, iEnd = pointAttributes.length; i < iEnd; ++i) {
            const attribute = pointAttributes[i];
            attribute.setOffset(byteOffsetToStart + blockByteSize * i);
            attribute.setStride(byteStride);
        }
    }
    bindUniforms(componentRenderer) {
        this.bindings.offsetUniform.bind(componentRenderer);
        if (this.uniformsToBind & EUniformBinding.BindDisplaySettings) {
            this.bindings.displayConfigUniform.bind(componentRenderer);
        }
        if (this.uniformsToBind & EUniformBinding.BindSizeTransform) {
            this.bindings.sizeTransformUniform.bind(componentRenderer);
        }
    }
    bindAttributes(componentRenderer) {
        const pointAttributes = this.bindings.pointAttributes;
        for (let i = 0, iEnd = pointAttributes.length; i < iEnd; ++i) {
            pointAttributes[i].bindArray(componentRenderer);
        }
    }
    bindInstanced(componentRenderer, divisor, usage) {
        const pointAttributes = this.bindings.pointAttributes;
        for (let i = 0, iEnd = pointAttributes.length; i < iEnd; ++i) {
            pointAttributes[i].bindArrayInstanced(componentRenderer, divisor, usage);
        }
    }
    getBindings(bindingDescriptor, binderConfig) {
        const blockElementCount = bindingDescriptor.blockElementCount;
        const byteOffset = bindingDescriptor.byteOffset;
        const pointAttributes = [
            new core_1.GlFloatAttribute("interleavedPoint_point", new core_1.GlFloatBuffer(null), blockElementCount, byteOffset),
        ];
        const sharedPointState = pointAttributes[0].getSharableState();
        if (!GlInterleaved2dPointBinder.isTransformConfig(binderConfig)) {
            DEBUG_MODE && rc_js_util_1._Debug.assert(binderConfig.pointsToBind > 0, "expected to bind points");
            for (let i = 1, iEnd = binderConfig.pointsToBind; i < iEnd; ++i) {
                const attribute = new core_1.GlFloatAttribute(`interleavedPoint_point${i}`, new core_1.GlFloatBuffer(null), blockElementCount, byteOffset * i);
                attribute.link(sharedPointState);
                pointAttributes.push(attribute);
            }
        }
        return {
            pointAttributes: pointAttributes,
            displayConfigUniform: new core_1.GlMat4Uniform("interleavedPoint_config", rc_js_util_1.Mat4.f32.factory.createOneEmpty()),
            sizeTransformUniform: new core_1.GlMat2Uniform("interleavedPoint_sizeTransform", rc_js_util_1.Mat2.f32.factory.createOneEmpty()),
            offsetUniform: new core_1.GlIVec4Uniform("interleavedPoint_offsets", this.offsets),
        };
    }
    getChangeId(entity) {
        return entity.changeId;
    }
    static generateProgram(bindingDescriptor, binderConfig) {
        const isTransformConfig = GlInterleaved2dPointBinder.isTransformConfig(binderConfig);
        const vertexShaders = [
            GlInterleaved2dPointBinder.getPreprocessorConstants(bindingDescriptor, isTransformConfig),
            core_1.unpackColorShader,
            core_1.mat2MultiplyValueShader,
            pointConnectorShader,
            generateAccessor(undefined),
            outputShader,
        ];
        if (!isTransformConfig) {
            for (let i = 1, iEnd = binderConfig.pointsToBind; i < iEnd; ++i) {
                vertexShaders.push(generateAccessor(i));
            }
        }
        return new core_1.GlProgramSpecification(core_1.GlShader.combineShaders(vertexShaders), core_1.emptyShader, rc_js_util_1._Array.emptyArray, rc_js_util_1._Array.emptyArray, isTransformConfig ? ["interleavedPoint_outPoint"] : rc_js_util_1._Array.emptyArray);
    }
    static getPreprocessorConstants(bindingDescriptor, bindsOutput) {
        const preprocessorStatements = [
            core_1.GlShader.getShaderFlag("INTERLEAVED_BINDS_OUTPUT", bindsOutput),
            core_1.GlShader.getShaderFlag("HAS_COLOR_OFFSET", bindingDescriptor.offsets.color != null),
            core_1.GlShader.getShaderFlag("HAS_SIZE_OFFSET", bindingDescriptor.offsets.size != null),
        ];
        return new core_1.GlShader(preprocessorStatements.join("\n"));
    }
    static getUniformsToBind(offsets) {
        const shouldBindDisplaySettings = (offsets.color == null || offsets.size == null)
            ? EUniformBinding.BindDisplaySettings
            : 0;
        const shouldBindSizeTransform = (offsets.size != null)
            ? EUniformBinding.BindSizeTransform
            : 0;
        return shouldBindDisplaySettings | shouldBindSizeTransform;
    }
    static isTransformConfig(config) {
        return Boolean(config.bindsOutput);
    }
    static generateOverrides(entity, segments) {
        const color = entity.graphicsSettings.pointDisplay.packedHighlightColor;
        const colorOverrides = new Array(segments.size * 2);
        let index = 0;
        for (const segmentId of segments) {
            // FIXME generate less garbage?
            colorOverrides[index] = [segmentId, new Float32Array([color])];
            colorOverrides[index + 1] = [segmentId + 1, new Float32Array([color])];
            index += 2;
        }
        return colorOverrides;
    }
}
(0, tslib_1.__decorate)([
    rc_js_util_1.Once
], GlInterleaved2dPointBinder.prototype, "getBinderId", null);
exports.GlInterleaved2dPointBinder = GlInterleaved2dPointBinder;
const pointConnectorShader = new core_1.GlShader(
// @formatter:off
`
/* === interleaved point connector === */
uniform highp mat4 interleavedPoint_config;
uniform highp mat2 interleavedPoint_sizeTransform;
uniform lowp ivec4 interleavedPoint_offsets;

#define GET_POSITION(NAME, SOURCE) vec2 NAME (void) \\
{ \\
    return vec2( SOURCE [interleavedPoint_offsets.x], SOURCE [interleavedPoint_offsets.y]); \\
}
#define SET_POSITION(NAME, DESTINATION) void NAME (vec2 position) \\
{ \\
    DESTINATION[interleavedPoint_offsets.x] = position.x; \\
    DESTINATION[interleavedPoint_offsets.y] = position.y; \\
}
#define GET_SIZE_FROM_UNIFORM(NAME) float NAME (void) \\
{ \\
    return interleavedPoint_config[0][0]; \\
}
#define GET_SIZE_FROM_ATTRIBUTE(NAME, SOURCE) float NAME (void) \\
{ \\
    return mat2MultiplyValue(interleavedPoint_sizeTransform, SOURCE [interleavedPoint_offsets.z]); \\
}
# define GET_COLOR_FROM_UNIFORM(NAME) vec4 NAME (void) \\
{ \\
    return vec4(interleavedPoint_config[1]); \\
}
# define GET_COLOR_FROM_ATTRIBUTE(NAME, SOURCE) vec4 NAME (void) \\
{ \\
 return unpackColor( SOURCE [interleavedPoint_offsets.w]); \\
}
`);
function generateAccessor(index) {
    const postfix = index == null ? "" : index.toString();
    return new core_1.GlShader(`
ATTRIBUTE highp vec4 interleavedPoint_point${postfix};

GET_POSITION(pointConnector_getPosition${postfix}, interleavedPoint_point${postfix})

#if HAS_SIZE_OFFSET
GET_SIZE_FROM_ATTRIBUTE(pointConnector_getSize${postfix}, interleavedPoint_point${postfix})
#else
GET_SIZE_FROM_UNIFORM(pointConnector_getSize${postfix})
#endif

#if HAS_COLOR_OFFSET
GET_COLOR_FROM_ATTRIBUTE(pointConnector_getColor${postfix}, interleavedPoint_point${postfix})
#else
GET_COLOR_FROM_UNIFORM(pointConnector_getColor${postfix})
#endif
`);
}
const outputShader = new core_1.GlShader(`
#if INTERLEAVED_BINDS_OUTPUT
VARYING vec4 interleavedPoint_outPoint;
void pointConnector_copyAllOutputs()
{
    interleavedPoint_outPoint = interleavedPoint_point;
}
SET_POSITION(pointConnector_setPosition, interleavedPoint_outPoint)
#endif
`);
var EUniformBinding;
(function (EUniformBinding) {
    EUniformBinding[EUniformBinding["BindDisplaySettings"] = 1] = "BindDisplaySettings";
    EUniformBinding[EUniformBinding["BindSizeTransform"] = 2] = "BindSizeTransform";
})(EUniformBinding || (EUniformBinding = {}));
const linkId = Symbol("cart-trace-2d");
/**
 * @internal
 */
class GlInterleaved2dPointTransformBinder extends GlInterleaved2dPointBinder {
    getTransformId() {
        // a vec4 is always used and copied before hand, so it's safe to share the transform regardless
        return "interleavedPoint2d";
    }
    setResultBuffers(entity, binder, componentRenderer, usage) {
        DEBUG_MODE && rc_js_util_1._Debug.runBlock(() => {
            const buffersAreDifferent = this.bindings.pointAttributes[0].getBuffer() !== binder.bindings.pointAttributes[0].getBuffer();
            rc_js_util_1._Debug.assert(buffersAreDifferent, "attempted to bind input to output");
        });
        const theirAttributes = binder.bindings.pointAttributes;
        const bufferByteSize = entity.data.getBlockByteSize() * entity.data.getLength();
        const changeId = entity.changeId;
        // only 1 buffer used during transform as the buffer is shared
        theirAttributes[0].setSize(componentRenderer.context, bufferByteSize, usage, changeId);
        theirAttributes[0].bindTransform(componentRenderer, 0);
    }
    clearResultBuffers(componentRenderer) {
        componentRenderer.context.bindBufferBase(componentRenderer.context.TRANSFORM_FEEDBACK_BUFFER, 0, null);
    }
    resetState() {
        // all attributes share the same buffer
        this.bindings.pointAttributes[0]
            .getBuffer()
            .resetState();
    }
}
//# sourceMappingURL=gl-interleaved-2d-point-binder.js.map